import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { IotmateButton } from "../../components/Shared";
import "./IrrigationSettings.css";

const IrrigationSettings = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    flowRate: 10,
    gatewayFrequency: 915,
    solenoidScheduling: "manual",
    pressureMaintenance: 2.5,
    openDuration: 10,
    restDuration: 5,
    cyclesPerDay: 3,
    startDate: "",
    endDate: ""
  });

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = () => {
    // Save settings (in real app, would send to API)
    console.log("Saving settings:", settings);
    navigate(-1);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="irrigation-settings-container">
      {/* Header */}
      <div className="irrigation-settings-header">
        <button onClick={handleBack} className="irrigation-settings-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="irrigation-settings-title-section">
          <h1 className="irrigation-settings-title">Irrigation Unit Settings</h1>
          <p className="irrigation-settings-subtitle">Device ID: {deviceId}</p>
        </div>
      </div>

      {/* Settings Card */}
      <div className="irrigation-settings-card">
        <div className="settings-form">
          <div className="settings-section">
            <h3 className="settings-section-title">General Settings</h3>

            <div className="settings-field">
              <label className="settings-label">Flow Rate (L/min)</label>
              <input
                type="number"
                className="settings-input"
                value={settings.flowRate}
                onChange={(e) => handleSettingChange('flowRate', parseFloat(e.target.value))}
                min="0"
                step="0.5"
              />
              <span className="settings-hint">Set the target flow rate for irrigation</span>
            </div>

            <div className="settings-field">
              <label className="settings-label">Gateway Frequency (MHz)</label>
              <select
                className="settings-input"
                value={settings.gatewayFrequency}
                onChange={(e) => handleSettingChange('gatewayFrequency', parseInt(e.target.value))}
              >
                <option value={433}>433 MHz</option>
                <option value={868}>868 MHz</option>
                <option value={915}>915 MHz</option>
                <option value={923}>923 MHz</option>
              </select>
              <span className="settings-hint">LoRa gateway communication frequency</span>
            </div>

            <div className="settings-field">
              <label className="settings-label">Pressure Maintenance (Bar)</label>
              <input
                type="number"
                className="settings-input"
                value={settings.pressureMaintenance}
                onChange={(e) => handleSettingChange('pressureMaintenance', parseFloat(e.target.value))}
                min="0"
                max="10"
                step="0.1"
              />
              <span className="settings-hint">Target pressure level to maintain in the system</span>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Solenoid Scheduling</h3>

            <div className="settings-field">
              <label className="settings-label">Scheduling Mode</label>
              <select
                className="settings-input"
                value={settings.solenoidScheduling}
                onChange={(e) => handleSettingChange('solenoidScheduling', e.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="time">Time-based</option>
              </select>
              <span className="settings-hint">Control mode for solenoid valves</span>
            </div>

            {settings.solenoidScheduling === "time" && (
              <>
                <div className="settings-schedule-info">
                  <span className="settings-schedule-note">
                    Solenoids will open one at a time in sequence. When duration ends, current solenoid closes and next one opens.
                  </span>
                </div>

                <div className="settings-field">
                  <label className="settings-label">Open Duration (Minutes)</label>
                  <input
                    type="number"
                    className="settings-input"
                    value={settings.openDuration}
                    onChange={(e) => handleSettingChange('openDuration', parseInt(e.target.value))}
                    min="1"
                    step="1"
                  />
                  <span className="settings-hint">How long each solenoid stays open before switching to next</span>
                </div>

                <div className="settings-field">
                  <label className="settings-label">Rest Duration (Minutes)</label>
                  <input
                    type="number"
                    className="settings-input"
                    value={settings.restDuration}
                    onChange={(e) => handleSettingChange('restDuration', parseInt(e.target.value))}
                    min="0"
                    step="1"
                  />
                  <span className="settings-hint">Rest time between closing one solenoid and opening next (0 for immediate switch)</span>
                </div>

                <div className="settings-field">
                  <label className="settings-label">Cycles Per Day</label>
                  <input
                    type="number"
                    className="settings-input"
                    value={settings.cyclesPerDay}
                    onChange={(e) => handleSettingChange('cyclesPerDay', parseInt(e.target.value))}
                    min="1"
                    step="1"
                  />
                  <span className="settings-hint">Number of complete cycles through all solenoids per day</span>
                </div>

                <div className="settings-row">
                  <div className="settings-field">
                    <label className="settings-label">Start Date</label>
                    <input
                      type="date"
                      className="settings-input"
                      value={settings.startDate}
                      onChange={(e) => handleSettingChange('startDate', e.target.value)}
                    />
                  </div>

                  <div className="settings-field">
                    <label className="settings-label">End Date</label>
                    <input
                      type="date"
                      className="settings-input"
                      value={settings.endDate}
                      onChange={(e) => handleSettingChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
                <span className="settings-hint">Schedule will repeat daily between these dates</span>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="settings-actions">
            <IotmateButton onClick={handleBack} variant="default">
              Cancel
            </IotmateButton>
            <IotmateButton onClick={handleSaveSettings} variant="primary">
              <FiSave size={16} />
              Save Settings
            </IotmateButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IrrigationSettings;
